import type { FilterItem, KeyedArray } from "@repo/models";

import { useMemo } from "react";

import type { idType } from ".";
import type { MarketFilterItem } from "../models";

import { GridFiltersController } from "../api/gridFiltersController";
import { useFilterItemQuery } from "../api/queries";
import { COMPANIES, DIVISIONS, MANAGEMENT_AREAS, MARKETS, REGIONS } from "../api/queryKeys";

const emptyCompanySegment: ICompanySegment = {};

export function useCompanySegmentFilterCascaded<T = KeyedArray<FilterItem>>(
	segmentProp: ICompanySegment,
	mapItems?: (i: KeyedArray<FilterItem>) => T,
) {
	const { companyId, companyIds, companyDivisionId, companyDivisionIds, companyRegionId, companyRegionIds } =
		segmentProp ?? emptyCompanySegment;
	const [companies, companiesLoaded] = useFilterItemQuery([COMPANIES], GridFiltersController.getCompanies);
	const [divisions, divisionsLoaded] = useFilterItemQuery([DIVISIONS], GridFiltersController.getDivisions);
	const [regions, regionsLoaded] = useFilterItemQuery([REGIONS], GridFiltersController.getRegions);
	const [markets, marketsLoaded] = useFilterItemQuery<MarketFilterItem>([MARKETS], GridFiltersController.getMarkets);
	const [managementAreas, managementAreasLoaded] = useFilterItemQuery<MarketFilterItem>(
		[MANAGEMENT_AREAS],
		GridFiltersController.getManagementAreas,
	);

	const marketsOnCompany = useMemo(
		() => filterMarkets(markets?.data ?? [], { companyId, companyIds }),
		[markets, companyId, companyIds],
	);
	const marketsOnDivision = useMemo(
		() => filterMarkets(marketsOnCompany, { companyDivisionId, companyDivisionIds }),
		[marketsOnCompany, companyDivisionId, companyDivisionIds],
	);
	const marketsOnRegion = useMemo(
		() => filterMarkets(marketsOnDivision, { companyRegionId, companyRegionIds }),
		[marketsOnDivision, companyRegionId, companyRegionIds],
	);

	const managementAreasOnRegion = useMemo(
		() =>
			filterMarkets(managementAreas?.data ?? [], {
				companyId,
				companyIds,
				companyDivisionId,
				companyDivisionIds,
				companyRegionId,
				companyRegionIds,
			}),
		[
			managementAreas,
			companyId,
			companyIds,
			companyDivisionId,
			companyDivisionIds,
			companyRegionId,
			companyRegionIds,
		],
	);

	return useMemo(
		() =>
			({
				companies,
				divisions: relevantItems(marketsOnCompany, divisions, (m) => m.divisionId, mapItems),
				regions: relevantItems(marketsOnDivision, regions, (m) => m.regionId, mapItems),
				markets: relevantItems(marketsOnRegion, markets, (m) => m.id, mapItems),
				managementAreas: relevantItems(managementAreasOnRegion, managementAreas, (m) => m.id, mapItems),
				companiesLoaded,
				divisionsLoaded,
				regionsLoaded,
				marketsLoaded,
				managementAreasLoaded,
			}) as ICompanySegmentList<T>,
		[
			companies,
			divisions,
			regions,
			markets,
			managementAreas,
			marketsOnCompany,
			marketsOnDivision,
			marketsOnRegion,
			managementAreasOnRegion,
			mapItems,
			companiesLoaded,
			divisionsLoaded,
			regionsLoaded,
			marketsLoaded,
			managementAreasLoaded,
		],
	);
}

function filterMarkets(markets: MarketFilterItem[], filter: Partial<ICompanySegment>) {
	if (!markets.length) return markets;
	return markets
		.filter((m) => !filter.companyId || m.companyId === filter.companyId)
		.filter((m) => !filter.companyDivisionId || m.divisionId === filter.companyDivisionId)
		.filter((m) => !filter.companyRegionId || m.regionId === filter.companyRegionId)
		.filter((m) => !filter.companyIds?.length || filter.companyIds.includes(m.companyId))
		.filter((m) => !filter.companyDivisionIds?.length || filter.companyDivisionIds.includes(m.divisionId))
		.filter((m) => !filter.companyRegionIds?.length || filter.companyRegionIds.includes(m.regionId));
}

/** The items any of `markets` points at; every item when no market matched. */
function relevantItems<T>(
	markets: MarketFilterItem[],
	allItems: KeyedArray<FilterItem>,
	selector: (m: MarketFilterItem) => number,
	mapItems?: (i: KeyedArray<FilterItem>) => T,
) {
	if (!markets.length) return mapItems ? mapItems(allItems) : allItems;
	const groups = markets.groupBy(selector);
	const items = allItems.filter((i) => groups.has(i.id));
	return mapItems ? mapItems(items) : items;
}

export interface ICompanySegment {
	companyId?: idType;
	companyDivisionId?: idType;
	companyRegionId?: idType;
	companyMarketId?: idType;
	companyManagementAreaId?: idType;

	companyIds?: idType[];
	companyDivisionIds?: idType[];
	companyRegionIds?: idType[];
	companyMarketIds?: idType[];
	companyManagementAreaIds?: idType[];
}

export interface ICompanySegmentList<T = KeyedArray<FilterItem>> {
	companies: T;
	divisions: T;
	regions: T;
	markets: T;
	managementAreas: T;
	companiesLoaded: boolean;
	divisionsLoaded: boolean;
	regionsLoaded: boolean;
	marketsLoaded: boolean;
	managementAreasLoaded: boolean;
}
